import React, { useContext } from 'react';
import PropTypes from 'prop-types';
import AppContext from 'context/Context';
import { useSendMessageLogic } from './useSendMessageLogic';
import SlashCommandsPopover from './SlashCommandsPopover';
import ComposerHeader from './ComposerHeader';
import ComposerFilePreview from './ComposerFilePreview';
import ComposerFooter from './ComposerFooter';
import ManageShortcutsModal from './ManageShortcutsModal';
import TicketNotesModal from '../TicketNotesModal';

const SendMessage = (props) => {
  const { config: { isDark } = {} } = useContext(AppContext);
  const logic = useSendMessageLogic(props);

  return (
    <div
      className="position-relative rounded-3 shadow-sm"
      style={{
        backgroundColor: isDark ? '#121e2d' : '#ffffff',
        border: isDark ? '1px solid #233142' : '1px solid #e2e8f0',
        transition: 'border-color 0.15s ease, background-color 0.15s ease'
      }}
    >
      {/* 1. Popover de autocompletado para Slash Commands al escribir '/' */}
      <SlashCommandsPopover
        showSuggestions={logic.showSuggestions}
        filteredCommands={logic.filteredCommands}
        selectedIndex={logic.selectedIndex}
        setSelectedIndex={logic.setSelectedIndex}
        applyCommand={logic.applyCommand}
        suggestionsRef={logic.suggestionsRef}
        isDark={isDark}
      />

      {/* 2. Barra Superior: Atajos rápidos */}
      <ComposerHeader
        hasOpenTickets={logic.hasOpenTickets}
        mergePillTitle={logic.mergePillTitle}
        mergePillLabel={logic.mergePillLabel}
        mergePromptMessage={logic.mergePromptMessage}
        displayQuickCommands={logic.displayQuickCommands}
        onShortcutClick={logic.handleShortcutClick}
        onOpenShortcutsModal={() => logic.setIsShortcutsModalOpen(true)}
        isDark={isDark}
      />

      {/* 3. Previsualización de archivo adjunto (imagen, video, audio o documento) */}
      <ComposerFilePreview
        filePreview={props.filePreview}
        onRemoveFile={logic.handleRemoveFile}
        isDark={isDark}
      />

      {/* 4. Área de Texto sin bordes internos */}
      <div className="px-3 pt-2 pb-1">
        <textarea
          ref={logic.textInputRef}
          rows={1}
          className="form-control border-0 p-0 shadow-none"
          style={{
            backgroundColor: 'transparent',
            color: isDark ? '#f9fafd' : '#0f172a',
            fontSize: '0.90rem',
            lineHeight: '1.45',
            resize: 'none',
            overflowY: 'auto',
            minHeight: '44px',
            maxHeight: '140px'
          }}
          placeholder={
            props.selectedFile
              ? 'Escribe un pie de foto o mensaje opcional...'
              : 'Escribe un mensaje o escribe "/" para atajos...'
          }
          value={props.inputText}
          onChange={e => props.setInputText(e.target.value)}
          onKeyDown={logic.handleKeyDown}
          onPaste={logic.handlePaste}
          autoFocus
        />
      </div>

      {/* 5. Barra Inferior: Botón '+' con menú, Formato, Emojis, Shift+Enter y Enviar */}
      <ComposerFooter
        isDark={isDark}
        onApplyFormat={logic.applyFormatWrapper}
        showEmojiPicker={logic.showEmojiPicker}
        setShowEmojiPicker={logic.setShowEmojiPicker}
        emojiPickerRef={logic.emojiPickerRef}
        onInsertEmoji={logic.insertEmoji}
        showPlusMenu={logic.showPlusMenu}
        setShowPlusMenu={logic.setShowPlusMenu}
        plusMenuRef={logic.plusMenuRef}
        onSelectPlusAction={logic.handleSelectPlusAction}
        isSending={props.isSending}
        canSend={logic.canSend}
        onSubmit={logic.handleSubmit}
      />

      {/* Input de archivo oculto */}
      <input
        type="file"
        ref={logic.fileInputRef}
        hidden
        accept="image/*,video/*"
        onChange={logic.handleFileChange}
      />

      {/* Modal para administrar o crear atajos */}
      {logic.isShortcutsModalOpen && (
        <ManageShortcutsModal
          show={logic.isShortcutsModalOpen}
          onClose={() => {
            logic.setIsShortcutsModalOpen(false);
            props.onReloadAtajos?.();
          }}
          onShortcutAdded={() => props.onReloadAtajos?.()}
        />
      )}

      {/* Modal para crear Nota Interna si se selecciona desde el botón '+' */}
      {logic.showTicketNoteModal && (
        <TicketNotesModal
          show={logic.showTicketNoteModal}
          onClose={() => logic.setShowTicketNoteModal(false)}
          ticket={props.ticket || {}}
          createNote={props.onCreateTicketNote}
          updateNote={props.onUpdateTicketNote || (() => {})}
          isSavingNote={props.isSavingNote}
        />
      )}
    </div>
  );
};

SendMessage.propTypes = {
  inputText: PropTypes.string.isRequired,
  setInputText: PropTypes.func.isRequired,
  onSendMessage: PropTypes.func.isRequired,
  selectedFile: PropTypes.object,
  setSelectedFile: PropTypes.func.isRequired,
  filePreview: PropTypes.object,
  setFilePreview: PropTypes.func.isRequired,
  isSending: PropTypes.bool.isRequired,
  commands: PropTypes.array,
  quickCommands: PropTypes.array,
  onSelectCommand: PropTypes.func,
  onReloadAtajos: PropTypes.func,
  clientOpenTickets: PropTypes.array,
  onOpenMergeModal: PropTypes.func,
  inputRef: PropTypes.object,
  ticket: PropTypes.object,
  onCreateTicketNote: PropTypes.func,
  onUpdateTicketNote: PropTypes.func,
  isSavingNote: PropTypes.bool
};

export default SendMessage;
